import { createContext, useContext, useEffect, type ReactNode } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";
import { EmptyState, ErrorState, LoadingState, Page } from "@marekl/adminer/components";
import { Building2 } from "lucide-react";
import { ApiError } from "./api";
import { atLeast, useCompanies, useCompany, type Company, type CompanyRole } from "./queries/companies";
import { useIsAdmin } from "../auth/useAuth";

/**
 * The company every page under /c/:companyId works in — the tenant. The id
 * lives in the URL (a link names its company; two tabs can hold two), the
 * last one used in localStorage for the start page.
 */
interface CompanyContextValue {
  company: Company;
  id: string;
  role: CompanyRole;
  /** The caller's role is at least `want` in this company. */
  can: (want: CompanyRole) => boolean;
}

const CompanyContext = createContext<CompanyContextValue | null>(null);

const LAST_KEY = "faktio.company";

function rememberCompany(id: string) {
  try {
    localStorage.setItem(LAST_KEY, id);
  } catch {
    // private mode: the start page falls back to the first company
  }
}

function lastCompany(): string | null {
  try {
    return localStorage.getItem(LAST_KEY);
  } catch {
    return null;
  }
}

export function useCurrentCompany(): CompanyContextValue {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error("useCurrentCompany outside a /c/:companyId route");
  return ctx;
}

/** The company of the route, when the route has one (for the shell). */
export function useOptionalCompany(): CompanyContextValue | null {
  return useContext(CompanyContext);
}

/** Layout route for /c/:companyId — loads the company, then its pages. */
export function CompanyScope() {
  const { companyId = "" } = useParams();
  const company = useCompany(companyId);

  useEffect(() => {
    if (company.data) rememberCompany(company.data.id);
  }, [company.data]);

  if (company.isLoading) return <LoadingState />;
  if (company.error) {
    const notFound = company.error instanceof ApiError && company.error.status === 404;
    return (
      <Page>
        <Page.Content>
          {notFound ? (
            <EmptyState icon={Building2} title="Firma nenalezena" description="Firma neexistuje, nebo do ní nemáte přístup." />
          ) : (
            <ErrorState title="Firmu se nepodařilo načíst" error={company.error} onRetry={company.refetch} />
          )}
        </Page.Content>
      </Page>
    );
  }
  const c = company.data!;
  return (
    <CompanyContext.Provider value={{ company: c, id: c.id, role: c.role, can: (want) => atLeast(c.role, want) }}>
      <Outlet />
    </CompanyContext.Provider>
  );
}

/** Renders children only when the caller's company role is at least `want`. */
export function IfCompanyRole({ want, children }: { want: CompanyRole; children: ReactNode }) {
  const { can } = useCurrentCompany();
  return can(want) ? <>{children}</> : null;
}

/** The start page: the last company used, else the first, else setup. */
export function StartRedirect() {
  const companies = useCompanies();
  const isAdmin = useIsAdmin();
  if (companies.isLoading) return <LoadingState />;
  const list = companies.data ?? [];
  const last = lastCompany();
  const target = list.find((c) => c.id === last) ?? list[0];
  if (target) return <Navigate to={`/c/${target.id}/overview`} replace />;
  if (isAdmin) return <Navigate to="/companies" replace />;
  return (
    <Page>
      <Page.Content>
        <EmptyState
          icon={Building2}
          title="Nemáte přístup k žádné firmě"
          description="Požádejte správce, aby vás přidal do firmy."
        />
      </Page.Content>
    </Page>
  );
}
